import { Box, makeStyles, Typography } from '@material-ui/core'
import { BAR_ITEM, EVENT } from '../../../utils/marketing'
import { gray, purple } from '../../../utils/colors'
import React from 'react'

/**
 * How a record renders in the client app, at 1:1 — every number lifted from clientmobile
 * Note: This can drift if the clientmobile code changes.
 */

const HABORO = "'Haboro Soft', sans-serif"

// clientmobile/src/Dashboard/constants.js
const CARD_RADIUS = 10
const EVENT_CARD_WIDTH = 270
const EVENT_CARD_IMAGE_HEIGHT = 210
const EVENT_IMAGE_ASPECT = 2

const FULL_CARD_WIDTH = 354

const EVENT_IMAGE_WIDTH = EVENT_CARD_IMAGE_HEIGHT * EVENT_IMAGE_ASPECT

const OFFER_CARD_WIDTH = 266
const OFFER_PADDING = 16

const useStyles = makeStyles(() => ({
	row: { display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' },
	caption: { marginBottom: 8, color: gray[700] },

	eventCard: { overflow: 'hidden' },
	eventTop: {
		height: EVENT_CARD_IMAGE_HEIGHT,
		borderTopLeftRadius: CARD_RADIUS,
		borderTopRightRadius: CARD_RADIUS,
		overflow: 'hidden',
		backgroundColor: purple[800],
		position: 'relative'
	},
	eventImage: {
		position: 'absolute',
		left: 0,
		top: 0,
		height: '100%',
		width: EVENT_IMAGE_WIDTH,
		objectFit: 'cover',
		objectPosition: 'left top'
	},
	eventFooter: {
		height: 50,
		boxSizing: 'border-box',
		backgroundColor: gray[100],
		border: `1px solid ${gray[400]}`,
		borderBottomLeftRadius: CARD_RADIUS,
		borderBottomRightRadius: CARD_RADIUS,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingLeft: 17,
		paddingRight: 17
	},
	footerLabel: {
		flexShrink: 1,
		fontFamily: HABORO,
		fontWeight: 600,
		fontSize: 14,
		color: 'rgba(0, 0, 0, 0.53)',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	footerAction: {
		fontFamily: HABORO,
		fontWeight: 700,
		fontSize: 14,
		color: purple[800],
		marginLeft: 12,
		whiteSpace: 'nowrap'
	},

	barCard: {
		width: FULL_CARD_WIDTH,
		borderRadius: CARD_RADIUS,
		overflow: 'hidden',
		backgroundColor: purple[800]
	},
	barImage: { width: '100%', display: 'block' },

	offerCard: {
		width: OFFER_CARD_WIDTH,
		boxSizing: 'border-box',
		backgroundColor: gray[200],
		borderRadius: CARD_RADIUS,
		padding: OFFER_PADDING
	},
	offerTopRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: 80,
		marginBottom: 12
	},
	offerLogo: { maxWidth: 120, maxHeight: 80, objectFit: 'contain', display: 'block' },
	offerPill: {
		backgroundColor: purple[500],
		borderRadius: 20,
		padding: '6px 12px',
		fontFamily: HABORO,
		fontWeight: 900,
		fontSize: 12,
		color: gray[100],
		whiteSpace: 'nowrap'
	},
	offerHeadline: {
		fontFamily: HABORO,
		fontWeight: 800,
		fontSize: 20,
		color: purple[900],
		marginBottom: 12
	},
	offerDescription: {
		fontFamily: HABORO,
		fontWeight: 400,
		fontSize: 14,
		lineHeight: '20px',
		color: gray[700],
		marginBottom: 12
	},
	offerLink: {
		fontFamily: HABORO,
		fontWeight: 400,
		fontSize: 14,
		color: purple[700],
		textDecoration: 'underline'
	},

	placeholder: {
		display: 'grid',
		placeItems: 'center',
		backgroundColor: '#EDE9F7',
		color: '#B00020',
		fontStyle: 'italic',
		fontSize: 13
	}
}))

/** Matches clientmobile's toSentenceCase */
const toSentenceCase = (value) => {
	if (!value) {
		return ''
	}

	return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

const EventCardPreview = ({ classes, draft, width }) => {
	// clientmobile renders no CTA when there is no action_text — the card is not tappable.
	const hasAction = Boolean(draft.action_text)

	return (
		<Box className={classes.eventCard} style={{ width }}>
			<Box className={classes.eventTop}>
				{draft.image_url ? (
					<img src={draft.image_url} alt="" className={classes.eventImage} />
				) : (
					<Box className={classes.placeholder} style={{ width: '100%', height: '100%' }}>
						No artwork
					</Box>
				)}
			</Box>
			<Box className={classes.eventFooter}>
				<span className={classes.footerLabel}>{draft.subheadline || ''}</span>
				{hasAction ? (
					<span className={classes.footerAction}>{toSentenceCase(draft.action_text)}</span>
				) : null}
			</Box>
		</Box>
	)
}

const MarketingContentPreview = ({ category, draft }) => {
	const classes = useStyles()

	if (category === EVENT) {
		return (
			<Box className={classes.row}>
				<Box>
					<Typography variant="caption" component="div" className={classes.caption}>
						Full width — the only event
					</Typography>
					<EventCardPreview classes={classes} draft={draft} width={FULL_CARD_WIDTH} />
				</Box>
				<Box>
					<Typography variant="caption" component="div" className={classes.caption}>
						In a carousel — two or more events
					</Typography>
					<EventCardPreview classes={classes} draft={draft} width={EVENT_CARD_WIDTH} />
				</Box>
			</Box>
		)
	}

	if (category === BAR_ITEM) {
		return (
			<Box>
				<Typography variant="caption" component="div" className={classes.caption}>
					Full width — scales to fit, never cropped
				</Typography>
				<Box className={classes.barCard}>
					{draft.image_url ? (
						<img src={draft.image_url} alt="" className={classes.barImage} />
					) : (
						<Box
							className={classes.placeholder}
							style={{ width: '100%', height: (FULL_CARD_WIDTH * 2) / 3 }}>
							No artwork
						</Box>
					)}
				</Box>
			</Box>
		)
	}

	return (
		<Box className={classes.offerCard}>
			<Box className={classes.offerTopRow}>
				{draft.partner_logo_url ? (
					<img src={draft.partner_logo_url} alt="" className={classes.offerLogo} />
				) : (
					<Box className={classes.placeholder} style={{ width: 120, height: 80 }}>
						No logo
					</Box>
				)}
				{draft.pill_text ? <span className={classes.offerPill}>{draft.pill_text}</span> : null}
			</Box>
			<div className={classes.offerHeadline}>{draft.headline || ''}</div>
			<div className={classes.offerDescription}>{draft.description || ''}</div>
			<div className={classes.offerLink}>{draft.action_text || 'Sign up today'}</div>
		</Box>
	)
}

export default MarketingContentPreview
